import { GenerateContractDraftError } from "../../../../types/onboarding";
import { stateLabel } from "../../../../utils/onboardingStates";

// Humanize a backend field identifier for display (no raw snake_case in copy).
const humanizeField = (name: string): string =>
  name
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

// The signing actions share the CWA-68 structured-error shape:
// { error, current_state?, required_states?, missing_fields?, invalid_fields? }.
type SigningError = GenerateContractDraftError;

/**
 * Map a backend contract-action rejection into a clear, non-technical message
 * for ops (FR-011). `fallback` is the action-specific generic message used when
 * the body carries no specifics (service error / unknown). Never surfaces raw
 * identifiers or enum tokens.
 */
export const contractErrorMessage = (
  err: any,
  fallback = "The action could not be completed. Please try again."
): string => {
  const body: SigningError | undefined = err?.response?.data;

  if (body?.missing_fields?.length) {
    const fields = body.missing_fields.map(humanizeField).join(", ");
    return `Required data is missing: ${fields}.`;
  }

  if (body?.invalid_fields?.length) {
    const parts = body.invalid_fields.map(
      (f) => `${humanizeField(f.field)} (${f.reason})`
    );
    return `Please fix: ${parts.join("; ")}.`;
  }

  if (body?.required_states?.length) {
    const need = body.required_states.map(stateLabel).join(" or ");
    return `Not allowed in the current status — the prospect must first reach: ${need}.`;
  }

  return fallback;
};

// Convenience wrappers with per-action fallbacks (used by the panel/dialogs).
export const sendContractErrorMessage = (err: any): string =>
  contractErrorMessage(
    err,
    "The contract could not be sent. Please try again."
  );

export const uploadSignedErrorMessage = (err: any): string =>
  contractErrorMessage(
    err,
    "The signed contract could not be recorded. Please check the file and try again."
  );

export const confirmSignedErrorMessage = (err: any): string =>
  contractErrorMessage(
    err,
    "The signature could not be confirmed. Please check the document link and try again."
  );
